import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { QueryState } from "@/shared/ui/QueryState";
import {
  useKPIAssignmentImpactQuery,
  useKPIQuery,
  useUpdateKPI,
} from "../api/queries";
import { KPIFormTour, useKPIFormTour } from "../tour/KPIFormTour";
import { KPIForm } from "../ui/KPIForm";
import { KPIImpactWarning } from "../ui/KPIImpactWarning";

export function KPIEditPage() {
  const nav = useNavigate({ from: "/app/kpi/$id/edit" });
  const { id } = useParams({ from: "/app/kpi/$id/edit" });
  const updateKPI = useUpdateKPI();
  const kpiDetail = useKPIQuery(id);
  const impactQuery = useKPIAssignmentImpactQuery(id);
  const tour = useKPIFormTour();

  return (
    <>
      <Header />
      <Main>
        <KPIFormTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ແກ້ໄຂ KPI</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground text-sm">
                ປັບປຸງລາຍລະອຽດຂອງ KPI
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => nav({ to: "/app/kpis" })}
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບ
            </Button>
          </div>

          <div className="rounded-lg border bg-card p-6">
            {kpiDetail.isLoading ? (
              <QueryState isLoading>ກຳລັງໂຫຼດ...</QueryState>
            ) : kpiDetail.error ? (
              <QueryState isError error={kpiDetail.error}>
                ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ KPI
              </QueryState>
            ) : kpiDetail.data ? (
              <>
                {impactQuery.data && impactQuery.data.assignmentCount > 0 && (
                  <KPIImpactWarning impact={impactQuery.data} />
                )}
                <KPIForm
                  variant="page"
                  initialValues={{
                    name: kpiDetail.data.name,
                    description: kpiDetail.data.description ?? "",
                    level: kpiDetail.data.level,
                    measurementMethod: kpiDetail.data.measurementMethod,
                    measurementSource: kpiDetail.data.measurementSource ?? "",
                    measurementPeriod: kpiDetail.data.measurementPeriod,
                    targetValue: kpiDetail.data.targetValue ?? undefined,
                    targetUnit: kpiDetail.data.targetUnit ?? undefined,
                    passCriteria: kpiDetail.data.passCriteria ?? "",
                    failCriteria: kpiDetail.data.failCriteria ?? "",
                    notes: kpiDetail.data.notes ?? "",
                    status: kpiDetail.data.status,
                  }}
                  onSubmit={async (vals) => {
                    await toast.promise(
                      updateKPI.mutateAsync({
                        id,
                        input: vals,
                      }),
                      {
                        loading: "ກຳລັງບັນທຶກ...",
                        success: "ບັນທຶກການປ່ຽນແປງສຳເລັດ",
                        error: "ບັນທຶກການປ່ຽນແປງລົ້ມເຫຼວ",
                      },
                    );
                    nav({ to: "/app/kpis" });
                  }}
                  submitting={updateKPI.isPending}
                  isEdit={true}
                />
              </>
            ) : (
              <div className="p-4 text-center text-destructive">ບໍ່ພົບຂໍ້ມູນ KPI</div>
            )}
          </div>
        </div>
      </Main>
    </>
  );
}
